import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { SelectPicker } from 'rsuite';
import { motion, AnimatePresence } from "framer-motion";
import MultiLingualContent from "../config/multilingualContent";
import { getPublishedSankeys, getSankeyDetails, getCountries } from "../functions";
import { LanguageContext } from "../config/multilingualContext";

import SankeyD3Chart from './SankeyD3Chart';
import SectionTitle from "./SectionTitle";
import YearSlider from './YearSlider';

import '../assets/css/flux.css';
import 'rsuite/dist/rsuite.min.css';

const LANG_FIELD = {
    french: 'label_fr',
    english: 'label_en',
    portuguais: 'label_pr',
    swahili: 'label_sw',
};

const FluxContent = () => {
    const { language } = useContext(LanguageContext);
    const [rawSankeys, setRawSankeys] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [years, setYears] = useState([]);
    const [currentYear, setCurrentYear] = useState(null);
    const dataCache = useRef({});
    const [playing, setPlaying] = useState(false);

    const publishedCharts = useMemo(() => rawSankeys.map(item => ({
        label: item[LANG_FIELD[language]] ?? item.label_fr ?? '',
        value: item.id,
        id: item.id,
    })), [rawSankeys, language]);

    useEffect(() => {
        async function fetchSankeys() {
            try {
                const countriesData = await getCountries();
                setSelectedCountry(countriesData?.length ? countriesData[0]?.id : null);

                const sankeyData = await getPublishedSankeys();
                setRawSankeys(sankeyData ?? []);
                setSelectedId(sankeyData?.length ? sankeyData[0].id : null);
            } catch (error) {
                console.log(error);
            }
        }
        fetchSankeys();
    }, []);

    useEffect(() => {
        async function fetchYearData() {
            if (!selectedId && !currentYear) return;
            try {
                let data = null;
                if (dataCache.current[selectedId]?.[currentYear]) {
                    data = dataCache.current[selectedId][currentYear];
                    setChartData(data);
                } else {
                    data = await getSankeyDetails(selectedId, currentYear, selectedCountry);
                    setChartData(data);
                    dataCache.current[selectedId] = {
                        ...dataCache.current[selectedId],
                        [currentYear]: data,
                    };
                }
                let allYears = data?.years? data.years: [];
                allYears = allYears.map(Number);
                 setYears(prev => {
                    if (prev.length === allYears.length && 
                        prev.every((y, i) => y === allYears[i])) {
                        return prev;
                    }
                    return allYears;
                });
                
                // ONLY set initial year on FIRST LOAD for this sankey/country
                // Do NOT reset when user manually changes year via slider
                if (currentYear === null || !allYears.includes(currentYear)) {
                    if (allYears.length > 0) {
                        setCurrentYear(allYears[allYears.length - 1]); // Set to the last year in the list
                    }
                }

            } catch (error) {
                console.log(error);
            }
        }
        fetchYearData();
     }, [currentYear, selectedId, selectedCountry]);


    return (
        <div className="container about-list-items">
            <SectionTitle pageTitle="sankey" />
            <div className="row mt-2">
                <div className="col-md-6 col-12">
                    <SelectPicker
                        label={<MultiLingualContent contentID="fluxTitle" />}
                        value={selectedId}
                        onChange={setSelectedId}
                        data={publishedCharts ?? []}
                        style={{ width: '100%' }}
                        
                    />
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-lg-12 col-xl-12 col-md-12 col-xs-12">
                    {chartData && chartData?.data.length !== 0 ? (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key="chart-container"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                <SankeyD3Chart json={chartData} language={language} />
                            </motion.div>
                        </AnimatePresence>
                    ) : (
                        <div className='row no-data-message'><MultiLingualContent contentID="nodata" /></div>
                    )}
                </div>
            </div>
            <div className="mt-3 mb-3">
            {years.length > 0 && (
                <YearSlider
                    years={years}
                    currentYear={currentYear}
                    setCurrentYear={setCurrentYear}
                    playing={playing}
                    setPlaying={setPlaying}
                />
            )}
            </div>
        </div>


    );
};

export default FluxContent;